import React, { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { loadCurrentUser } from './store/slices/authSlice';
import i18n, { resolveInitialLanguage } from './i18n';
import { useUpdateChecker } from './hooks/useUpdateChecker';
import PWAInstallPrompt from './components/common/PWAInstallPrompt';

const HomePage = lazy(() => import('./pages/HomePage'));
const ScanPage = lazy(() => import('./pages/ScanPage'));
const Login = lazy(() => import('./pages/Login'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const DashboardLayout = lazy(() => import('./components/layout/DashboardLayout'));
const AdminDashboard = lazy(() => import('./pages/admin/Dashboard'));
const TeacherDashboard = lazy(() => import('./pages/teacher/Dashboard'));
const ParentDashboard = lazy(() => import('./pages/parent/Dashboard'));
const StudentDashboard = lazy(() => import('./pages/student/Dashboard'));
const ProgressiveReportPrint = lazy(() => import('./pages/reports/ProgressiveReportPrint'));

const NotFound = lazy(() => import('./pages/NotFound'));

import LoadingScreen from './components/common/LoadingScreen';
import ChunkLoadErrorBoundary from './components/common/ChunkLoadErrorBoundary';

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useSelector((state) => state.auth);

  // Fresh document load (e.g. opening a report's print page in a new tab)
  // starts with a bare redux store: isAuthenticated is false and loading has
  // not flipped to true yet, so without this we'd bounce to /login before
  // loadCurrentUser() even dispatches. If a token is present, wait for the
  // session to hydrate instead of redirecting.
  let hasToken = false;
  try { hasToken = !!localStorage.getItem('accessToken'); } catch { /* storage blocked */ }

  if (loading || (hasToken && !isAuthenticated && !user)) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children || <Outlet />;
};

const RoleBasedRoute = ({ roles, children }) => {
  const { user } = useSelector((state) => state.auth);

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  const normalizedRole = (user.role || '').replace(/-/g, '_');
  const hasAccess = roles.includes(user.role) || roles.includes(normalizedRole);

  if (!hasAccess) {
    const dashboardMap = {
      'super_admin': '/admin/dashboard',
      teacher: '/teacher/dashboard',
      parent: '/parent/dashboard',
      student: '/student/dashboard',
    };
    return <Navigate to={dashboardMap[user.role] || '/dashboard'} replace />;
  }

  return children || <Outlet />;
};

const DashboardRedirect = () => {
  const { user } = useSelector((state) => state.auth);
  const routes = {
    'super_admin': '/admin/dashboard',
    teacher: '/teacher/dashboard',
    parent: '/parent/dashboard',
    student: '/student/dashboard',
  };
  return <Navigate to={routes[user?.role] || '/admin/dashboard'} replace />;
};

const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen bg-gray-50">
    <div className="text-center">
      <div className="w-16 h-16 border-4 border-gov-blue border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
      <p className="text-gray-500 text-sm">Loading...</p>
    </div>
  </div>
);

function App() {
  const dispatch = useDispatch();
  const { user: authUser } = useSelector((state) => state.auth);
  const token = localStorage.getItem('accessToken');

  useUpdateChecker();

  // Apply the default language for the authenticated user's role
  // (parents → Kinyarwanda, others → English) unless an explicit
  // choice has been stored.
  useEffect(() => {
    if (authUser?.role) {
      const lang = resolveInitialLanguage(authUser.role);
      if (i18n.language !== lang) {
        i18n.changeLanguage(lang);
      }
    }
  }, [authUser?.role]);

  useEffect(() => {
    if (token) {
      dispatch(loadCurrentUser());
    }
  }, [dispatch, token]);

  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <ChunkLoadErrorBoundary>
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/scan" element={<ScanPage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />

          <Route
            path="/reports/print/:reportId"
            element={
              <ProtectedRoute>
                <ProgressiveReportPrint />
              </ProtectedRoute>
            }
          />

          <Route
            element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/dashboard" element={<DashboardRedirect />} />

            <Route
              path="/admin/*"
              element={
                <RoleBasedRoute roles={['super_admin']}>
                  <AdminDashboard />
                </RoleBasedRoute>
              }
            />

            <Route
              path="/teacher/*"
              element={
                <RoleBasedRoute roles={['teacher']}>
                  <TeacherDashboard />
                </RoleBasedRoute>
              }
            />

            <Route
              path="/parent/*"
              element={
                <RoleBasedRoute roles={['parent']}>
                  <ParentDashboard />
                </RoleBasedRoute>
              }
            />

            <Route
              path="/student/*"
              element={
                <RoleBasedRoute roles={['student']}>
                  <StudentDashboard />
                </RoleBasedRoute>
              }
            />
          </Route>

          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
      </ChunkLoadErrorBoundary>

      <PWAInstallPrompt
        appName="STAR BMCL"
      />

      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        toastClassName="shadow-gov"
      />
    </BrowserRouter>
  );
}

export default App;
